import {
  TAssignTask,
  TAssignTaskFormData,
  TAssignTaskFormOptionsData,
  TFormGroupOption,
  TFormOption,
  TMember,
  TMemberOption,
  TSaveFormData,
  TTask,
  TTaskOption
} from './types';
import { ConfigurationFields } from './constants';

export const getInitialConfiguration = (): TAssignTaskFormData => ({
  [ConfigurationFields.DefaultTasks]: [],
  [ConfigurationFields.DefaultTeamMembers]: [],
  [ConfigurationFields.Comments]: ''
});

export const getInitialOptionsConfiguration = ({ members, tasks }: TAssignTask): TAssignTaskFormOptionsData => ({
  [ConfigurationFields.Tasks]: modifyTasksToGroupOption(tasks),
  [ConfigurationFields.TeamMembers]: modifyMembersToGroupOption(members)
});

export const getValuesForSave = (values: TAssignTaskFormData, originData: TAssignTask): Partial<TSaveFormData> => ({
  [ConfigurationFields.DefaultTasks]: modifyTasksForSave(values.defaultTasks, originData.tasks),
  [ConfigurationFields.DefaultTeamMembers]: modifyMembersForSave(values.defaultTeamMembers, originData.members)
});

const makeCollection = (data: object[], groupProp: string, itemsProp: string): Record<string, object[]> => {
  return data.reduce((acc: Record<string, object[]>, item: object) => {
    return {
      ...acc,
      [item[groupProp]]: [...(acc[item[groupProp]] || []), ...item[itemsProp]]
    };
  }, {});
};

const createUniqValueForOption = (groupName: string, value: string) => `${groupName}###${value}`;

const mapUniqValueForOption = (data: object[], groupName: string, valueProp: string, labelProp: string): TFormOption[] => {
  return data.map((item: object) => {
    return { label: item[labelProp], value: createUniqValueForOption(groupName, item[valueProp]) };
  });
};

const getGroupedDataFromOptions = (data: TFormOption[]) => {
  return data.reduce((acc: Record<string, string[]>, item: TFormOption) => {
    const splitData = item.value.split('###');
    const category = splitData[0];
    const id = splitData[1];
    
    return {
      ...acc,
      [category]: [...(acc[category] || []), id]
    };
  }, {});
};

export const modifyTasksToGroupOption = (data: object[]): TFormGroupOption[] => {
  const collection = makeCollection(data, 'name', 'values');
  
  return Object.keys(collection).reduce((result, key) => {
    result.push({
      label: key,
      options: [...mapUniqValueForOption(collection[key] as TFormOption[], key, 'id', 'title')],
    });
    
    return result;
  }, []);
};

export const modifyMembersToGroupOption = (data: object[]): TFormGroupOption[] => {
  const collection = makeCollection(data, 'userGroupName', 'values');
  
  return Object.keys(collection).reduce((result, key) => {
    result.push({
      label: key,
      options: collection[key].map((item) => {
        const a = item as TMemberOption;
        return {
          value: createUniqValueForOption(key, a.id),
          label: `${a.userName} (${a.email})`
        };
      }),
    });
    
    return result;
  }, []);
};

export const modifyMembersForSave = (selected: TFormOption[], originalList: TMember[]): TMember[] => {
  const grouped = getGroupedDataFromOptions(selected);
  
  return Object.keys(grouped).reduce((acc: TMember[], groupName: string) => {
    const group = originalList.find((item: TMember) => item.userGroupName === groupName);
    
    if (!group) {
      return acc;
    }
    
    acc.push({
      ...group,
      values: group.values.filter((item: TMemberOption) => grouped[groupName].includes(item.id))
    });
    
    return acc;
  }, []);
};

export const modifyTasksForSave = (selected: TFormOption[], originalList: TTask[]): TTask[] => {
  const grouped = getGroupedDataFromOptions(selected);
  
  return Object.keys(grouped).reduce((acc: TTask[], groupName: string) => {
    const group = originalList.find((item: TTask) => item.name === groupName);
    
    if (!group) {
      return acc;
    }
    
    acc.push({
      ...group,
      values: group.values.filter((item: TTaskOption) => grouped[groupName].includes(item.id))
    });
    
    return acc;
  }, []);
};
